import React, { useState, useEffect } from 'react';

import { DetectionIcon } from '@vlabs/icons';
import { SimilarityTag } from '@vlabs/shared/components/similarity-tag/SimilarityTag';
import { Control, FaceSample } from '@vlabs/uikit';
import { useTranslation } from 'react-i18next';

import st from './VerifyFaceForm.module.sass';
import {
  extractAttributes, getFaceAttributes, useMatchFaces,
} from '@vlabs/api/luna-api/client';
import type {
  AttributeMatchResult, DetectorFaceDetectionSample, FaceId, SampleId,
} from '@vlabs/api/luna-api/model';
import { FaceDetector } from '../../shared/ui/face-detector/FaceDetector';

interface VerifyFaceFormProps {
  faceId: FaceId;
}

function VerifyFaceForm({
  faceId,
}: VerifyFaceFormProps): React.JSX.Element {
  const { t } = useTranslation();
  const [referenceFaceSampleId, setReferenceFaceSampleId] = useState<SampleId>();
  const [candidateDetection, setCandidateDetection] = useState<DetectorFaceDetectionSample>();
  const [similarity, setSimilarity] = useState<number>();
  const $matchFaces = useMatchFaces({
    mutation: {
      onSuccess: (matchFaceResponse) => {
        const $similarity = (matchFaceResponse[0]?.matches?.[0] as AttributeMatchResult)?.result[0]?.similarity ?? 0;
        setSimilarity($similarity);
      },
    },
  });

  useEffect(() => {
    getFaceAttributes(faceId, { targets: ['face_descriptor_samples'] })
      .then((faceAttribute) => {
        if (faceAttribute.attributes.face_descriptor_samples?.[0]) {
          setReferenceFaceSampleId(faceAttribute.attributes.face_descriptor_samples[0]);
        }
      });
  }, [faceId]);

  const verify = async ($candidateDetection: DetectorFaceDetectionSample) => {
    if (!faceId || !$candidateDetection?.sample_id) return;
    const candidateAttribute = await extractAttributes([$candidateDetection.sample_id]);
    if (!candidateAttribute[0]?.attribute_id) return;

    $matchFaces.mutate({
      data: {
        candidates: [
          { filters: { origin: 'faces', face_ids: [faceId] } },
        ],
        references: [
          { type: 'attribute', id: candidateAttribute[0].attribute_id },
        ],
      },
    });
  };

  return (
    <div className={st.ModalContent}>
      <div className={st.Title}>{t('card-page:features.verify face.modal.title')}</div>
      <div className={st.SampleWrapper}>
        <FaceSample
          className={st.ReferenceSample}
          size="large"
          src={referenceFaceSampleId ? `/api/lp5/6/samples/faces/${referenceFaceSampleId}` : undefined}
        />

        {!!similarity && !!candidateDetection?.sample_id && (
          <FaceSample
            className={st.ReferenceSample}
            size="large"
            src={`/api/lp5/6/samples/faces/${candidateDetection.sample_id}`}
          />
        )}
        {(!similarity || !candidateDetection?.sample_id) && (
          <FaceDetector
            className={st.FaceDetector}
            onChange={(samples) => {
              if (!samples || samples.length === 0) return;
              if (samples.length === 1 && samples[0]) {
                setCandidateDetection(samples[0]);
              }
            }}
          />
        )}
        {!!similarity && (
          <SimilarityTag
            className={st.Tag}
            icon={<DetectionIcon />}
            size="large"
            value={similarity}
          />
        )}
      </div>
      <div className={st.Buttons}>
        <Control.Button
          disabled={!referenceFaceSampleId || !candidateDetection || similarity !== undefined}
          onClick={async () => verify(candidateDetection as DetectorFaceDetectionSample)}
        >
          {t('card-page:features.verify face.modal.buttons.verify.label')}
        </Control.Button>

        {!!similarity && (
          <Control.Button
            kind="secondary"
            onClick={() => {
              setCandidateDetection(undefined);
              setSimilarity(undefined);
            }}
            variant="outlined"
          >
            {t('card-page:features.verify face.modal.buttons.clear.label')}
          </Control.Button>
        )}
      </div>
    </div>
  );
}

export { VerifyFaceForm };
